/**
 * Leads Service
 * Handles all lead-related data operations with workspace-first RLS
 * 
 * AUDIT FIX (2025-01-16):
 * - Fixed: All queries now filter by archived = false (boolean, NOT archived_at)
 * - Schema: leads.archived (boolean), NOT leads.archived_at (timestamptz)
 * - This ensures archived leads are excluded from Today, Leads list, and all queries
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';
import { WorkspaceScope, applyWorkspaceScope } from '@/lib/workspaceScope';

export type Lead = Tables<'leads'>;

export interface CreateLeadInput extends Omit<TablesInsert<'leads'>, 'workspace_id'> {
  workspace_id: string;
}

export interface UpdateLeadInput extends Partial<TablesUpdate<'leads'>> {
  id: string;
  workspace_id: string;
}

export class LeadsService {
  /**
   * Get all leads for a workspace scope
   * @param scope - Workspace scope (single workspace or all accessible)
   * @param filters - Optional filters
   */
  static async getLeads(scope: WorkspaceScope, filters?: {
    stage?: string;
    temperature?: string;
    search?: string;
  }): Promise<(Lead & { workspace_name?: string })[]> {
    // In 'all' mode, include workspace name via join
    const selectFields = scope.type === 'all' 
      ? '*, workspaces!inner(name)' 
      : '*';
    
    let query = supabase
      .from('leads')
      .select(selectFields);

    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);

    // CRITICAL: Filter out archived leads (archived is boolean, NOT archived_at)
    // Schema: leads.archived (boolean), NOT leads.archived_at
    query = query.eq('archived', false);

    // Apply additional filters
    if (filters?.stage) {
      query = query.eq('pipeline_stage', filters.stage as any);
    }

    if (filters?.temperature) {
      query = query.eq('temperature', filters.temperature as any);
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,company.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      logger.error('[LeadsService] Error fetching leads', error, { scope, filters });
      
      // Handle RLS/permission errors gracefully - return empty array instead of throwing
      // This prevents 500 errors in UI when switching workspaces
      if (error.code === '42501' || error.code === 'PGRST301' || 
          error.message?.includes('permission') || error.message?.includes('RLS') ||
          error.message?.includes('row-level security')) {
        logger.warn('[LeadsService] Permission denied, returning empty array', { scope, filters });
        return [];
      }
      
      // For other errors, still throw but with better message
      throw toError(error, 'Failed to get leads');
    }

    // Map results: in 'all' mode, extract workspace name from join
    const leads = (data || []).map((lead: any) => {
      const baseLead = scope.type === 'all' && lead.workspaces
        ? { ...lead, workspace_name: lead.workspaces.name }
        : lead;
      // Remove workspaces object if present (it's from the join)
      const { workspaces, ...cleanLead } = baseLead;
      return cleanLead;
    }) as (Lead & { workspace_name?: string })[];

    // DEV LOGGING: Track query results
    if (process.env.NODE_ENV === 'development') {
      const archivedCount = leads.filter(l => (l as any).archived === true).length;
      const activeCount = leads.length - archivedCount;
      console.log('[LeadsService.getLeads] Query result:', {
        scope: scope.type === 'single' ? { type: 'single', workspaceId: scope.workspaceId } : { type: 'all', workspaceIds: scope.workspaceIds },
        totalLeads: leads.length,
        activeLeads: activeCount,
        archivedLeads: archivedCount,
        filters,
      });
    }

    return leads;
  }

  /**
   * Get single lead by ID (with workspace scope check)
   * Alias for getLeadById for consistency
   */
  static async getLeadById(leadId: string, scope: WorkspaceScope): Promise<Lead | null> {
    return this.getLead(leadId, scope);
  }

  /**
   * Get single lead by ID (with workspace scope check)
   */
  static async getLead(leadId: string, scope: WorkspaceScope): Promise<Lead | null> {
    let query = supabase
      .from('leads')
      .select('*')
      .eq('id', leadId);
    
    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);
    
    // CRITICAL: Filter out archived leads
    query = query.eq('archived', false);
    
    const { data, error } = await query.maybeSingle();

    if (error) {
      logger.error('[LeadsService] Error fetching lead', error, { leadId, scope });
      throw new Error(`Failed to get lead: ${error.message}`);
    }

    return data as Lead | null;
  }

  /**
   * Create a lead using RPC to bypass RLS stack overflow
   */
  static async createLead(input: CreateLeadInput): Promise<Lead> {
    const leadJson = {
      name: input.name || 'Unknown',
      email: input.email || null,
      phone: input.phone || null,
      company: input.company || null,
      notes_short: input.notes_short || null,
      notes_long: input.notes_long || null,
      source: input.source || 'manual',
      temperature: input.temperature || 'hot', // New leads start as HOT
      pipeline_stage: input.pipeline_stage || 'new',
      decision_score: input.decision_score ?? 0,
      tags: Array.isArray(input.tags) ? input.tags : (input.tags ? [input.tags] : []),
    };

    const { data: rpcResult, error: rpcError } = await supabase
      .rpc('insert_single_lead' as any, {
        lead_data: leadJson as any,
        workspace_id_param: input.workspace_id
      });

    if (rpcError) {
      logger.error('[LeadsService] RPC error creating lead', rpcError, { workspaceId: input.workspace_id });
      throw new Error(getSupabaseErrorMessage(rpcError));
    }

    if (!rpcResult) {
      throw new Error('No response from RPC');
    }

    const result = rpcResult as any;
    if (!result.success) {
      throw new Error(result.error || 'Failed to create lead');
    }

    return result.data as Lead;
  }

  /**
   * Update a lead
   */
  static async updateLead(input: UpdateLeadInput): Promise<Lead> {
    const { id, workspace_id, ...updates } = input;

    const { data, error } = await supabase
      .from('leads')
      .update(updates as any)
      .eq('id', id)
      .eq('workspace_id', workspace_id)
      .select()
      .single();

    if (error) {
      logger.error('[LeadsService] Error updating lead', error, { leadId: input.id, workspaceId: input.workspace_id });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Lead;
  }

  /**
   * Delete a lead
   */
  static async deleteLead(leadId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('leads')
      .delete()
      .eq('id', leadId)
      .eq('workspace_id', workspaceId);

    if (error) {
      logger.error('[LeadsService] Error deleting lead', error, { leadId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }
  }

  /**
   * Get multiple leads by IDs
   */
  static async getLeadsByIds(scope: WorkspaceScope, leadIds: string[]): Promise<Lead[]> {
    if (!leadIds || leadIds.length === 0) {
      return [];
    }

    let query = supabase
      .from('leads')
      .select('*')
      .in('id', leadIds);

    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);

    // CRITICAL: Filter out archived leads
    query = query.eq('archived', false);

    const { data, error } = await query;

    if (error) {
      logger.error('[LeadsService] Error fetching leads by IDs', error, { scope, leadIdsCount: leadIds.length });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return (data || []) as Lead[];
  }

  /**
   * Get leads with upcoming actions (follow-ups) in date range
   * Decision-first: uses next_follow_up_at, falls back to next_action_at
   */
  static async getLeadsWithUpcomingActions(
    scope: WorkspaceScope,
    startDate: string,
    endDate: string
  ): Promise<Lead[]> {
    let query = supabase
      .from('leads')
      .select('*')
      .or(`next_follow_up_at.gte.${startDate},next_follow_up_at.lte.${endDate},next_action_at.gte.${startDate},next_action_at.lte.${endDate}`);

    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);

    // CRITICAL: Filter out archived leads
    query = query.eq('archived', false);

    query = query
      .order('next_follow_up_at', { ascending: true, nullsLast: true })
      .order('next_action_at', { ascending: true, nullsLast: true });

    const { data, error } = await query;

    if (error) {
      logger.error('[LeadsService] Error fetching leads with upcoming actions', error, { scope, startDate, endDate });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return (data || []) as Lead[];
  }

  /**
   * Bulk create leads using RPC
   */
  static async bulkCreateLeads(leads: CreateLeadInput[], workspaceId: string): Promise<{ inserted: number; errors: any[] }> {
    const chunkSize = 50;
    const chunks: any[][] = [];
    
    for (let i = 0; i < leads.length; i += chunkSize) {
      chunks.push(leads.slice(i, i + chunkSize));
    }

    let totalInserted = 0;
    const allErrors: any[] = [];

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      const chunkJson = chunk.map(lead => ({
        name: lead.name || 'Unknown',
        email: lead.email || null,
        phone: lead.phone || null,
        company: lead.company || null,
        notes_short: lead.notes_short || null,
        notes_long: lead.notes_long || null,
        source: lead.source || 'manual',
        temperature: lead.temperature || 'hot', // New leads start as HOT
        pipeline_stage: lead.pipeline_stage || 'new',
        decision_score: lead.decision_score ?? 0,
        tags: Array.isArray(lead.tags) ? lead.tags : (lead.tags ? [lead.tags] : []),
      }));

      const { data: rpcResult, error: rpcError } = await supabase
        .rpc('bulk_insert_leads' as any, {
          leads_data: chunkJson as any,
          workspace_id_param: workspaceId
        });

      if (rpcError) {
        logger.error(`[LeadsService] Chunk ${i + 1} RPC error`, rpcError, { chunk: i + 1, workspaceId });
        allErrors.push({ chunk: i + 1, error: rpcError });
        continue;
      }

      const result = rpcResult as any;
      if (result && result.success) {
        totalInserted += result.inserted || 0;
        if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
          allErrors.push(...result.errors);
        }
      } else {
        allErrors.push({ chunk: i + 1, error: result?.error || 'Unknown error' });
      }
    }

    return { inserted: totalInserted, errors: allErrors };
  }
}

